import { useState, useEffect, useRef } from "react";
import { supabase, BRAND, BRAND_LIGHT, MUTED, card, text, btn, linkBtn, errorText, INK, TEXT, SURFACE } from "./constants";
import { LogoLoader, LOGO_DONE_MS } from "./LogoLoader";
import { evalCriteria } from "./shared";
import { aiFetch } from "./lib/ai";
import { getCvSignedUrl } from "./lib/cv";
import { useIsMobile } from "./lib/responsive";

// ─── GUÍA DE ENTREVISTA, SENCILLA ─────────────────────────────────────────────
// Sustituye a la pestaña Guide (InterviewerGuideTab, que sigue en el repo y en
// el tag ficha-candidato-v1). Va al lado de la evaluación y dice solo lo que
// hace falta en la entrevista:
//   1. Cómo abrir: intro y rompehielos.
//   2. Por cada pregunta de evaluación de la posición: qué preguntar para
//      poder puntuarla, y qué escuchar.
//   3. Cómo cerrar.
// Se genera una vez y se guarda en candidates.interviewer_guide (el mismo
// JSON de la guía antigua, con la clave simple_es / simple_en), así que no
// se regenera al entrar.

const parseStored = (v) => {
  if (!v) return {};
  if (typeof v === "object") return v;
  try { return JSON.parse(v) || {}; } catch (e) { return {}; }
};

// Mientras se lee o se prepara la guía (tarda unos segundos con la IA): el
// logo animado de LogoLoader.jsx con un texto debajo que va cambiando. Cuando
// la guía está lista (`done`), «Guía lista» y el único confeti de la web
// (Borja, 23/10). Con "reducir movimiento", quieto.
export const GUIDE_DONE_MS = LOGO_DONE_MS;
const MSG_CSS = `@keyframes rtGuideFade { 0% { opacity: 0; transform: translateY(4px) } 12%, 88% { opacity: 1; transform: none } 100% { opacity: 0 } }
.rt-guide-msg { animation: rtGuideFade 2.4s ease-in-out infinite }
@media (prefers-reduced-motion: reduce) { .rt-guide-msg { animation: none } }`;

export const GuideLoader = ({ lang, done = false }) => {
  const es = lang === "es";
  const steps = es
    ? ["Leyendo el CV y la cualificación", "Buscando lo que hay que aclarar", "Escribiendo las preguntas por vector"]
    : ["Reading the CV and qualification", "Finding what to probe", "Writing the questions per vector"];
  const [step, setStep] = useState(0);
  useEffect(() => {
    if (done) return undefined;
    const iv = setInterval(() => setStep(s => (s + 1) % steps.length), 2400);
    return () => clearInterval(iv);
  }, [steps.length, done]);
  return (
    <div role="status" aria-live="polite" style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "24px 0 8px", textAlign: "center" }}>
      <style>{MSG_CSS}</style>
      <LogoLoader done={done} />
      <div style={{ ...text.cardTitle, marginTop: 18 }}>{done ? (es ? "Guía lista" : "Guide ready") : (es ? "Preparando la guía" : "Preparing the guide")}</div>
      {!done && <div key={step} className="rt-guide-msg" style={{ ...text.bodySm, color: MUTED, marginTop: 4 }}>{steps[step]}…</div>}
      {done && <div style={{ ...text.bodySm, color: MUTED, marginTop: 4 }}>&nbsp;</div>}
    </div>
  );
};

// embedded: dentro de la ficha a ancho completo (sin quedarse fija)
// bare: dentro del panel lateral de Evaluación (sin tarjeta propia).
// onStored: avisa con el JSON guardado, para que la lista en memoria lo tenga
// y al volver a abrir el panel no pida generarla otra vez.
// focusSection: id del vector (c1…c4) del botón WTA (What to ask) de la
// evaluación. Se enseñan SOLO las preguntas de ese vector, con un enlace a la
// guía completa; si aún no hay guía, se prepara sola al abrir (13/10).
const SimpleGuide = ({ candidate, position, lang, embedded = false, bare = false, onStored = () => {}, focusSection = null, sameLangOnly = false }) => {
  const es = lang === "es";
  // Móvil: sin fijar, márgenes de 16 y botones de 44. El escritorio no cambia.
  const isMobile = useIsMobile();
  const [stored, setStored] = useState(() => parseStored(candidate.interviewerGuide));
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  // La guía se vuelve a leer de la base al abrir: la lista en memoria puede ser
  // una copia vieja (desde la ficha no se refresca) y entonces pedía generarla
  // otra vez aunque ya estuviera guardada.
  const [fetched, setFetched] = useState(false);
  const [showAll, setShowAll] = useState(!focusSection);
  // «Guía lista» con confeti, solo al generar la primera
  const [celebrate, setCelebrate] = useState(false);
  const celebrateTimer = useRef(null);
  const pendingStored = useRef(null);
  const flushStored = () => {
    if (pendingStored.current) { const json = pendingStored.current; pendingStored.current = null; onStored(json); }
  };
  useEffect(() => () => { clearTimeout(celebrateTimer.current); flushStored(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const key = `simple_${es ? "es" : "en"}`;
  // Se guarda una por idioma. Si no hay en el idioma de la pantalla pero sí en
  // el otro, se enseña esa (no hay que regenerar por cambiar de idioma) y se
  // ofrece generarla en este idioma solo si se quiere. Con `sameLangOnly` (la
  // entrevista a pantalla completa) no: se genera en el idioma de la pantalla.
  const otherKey = `simple_${es ? "en" : "es"}`;
  const guide = stored[key] || (sameLangOnly ? null : stored[otherKey]) || null;
  const fromOtherLang = !sameLangOnly && !stored[key] && !!stored[otherKey];
  const sections = evalCriteria(lang, position?.evalQuestions);
  useEffect(() => {
    let alive = true;
    supabase.from("candidates").select("interviewer_guide").eq("id", candidate.id).maybeSingle()
      .then(({ data }) => {
        if (!alive) return;
        const db = parseStored(data?.interviewer_guide);
        if (Object.keys(db).length) { setStored(db); onStored(JSON.stringify(db)); }
        setFetched(true);
      });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidate.id]);
  // Otro vector de WTA con el panel ya abierto: vuelve a enseñar solo ese
  useEffect(() => { setShowAll(!focusSection); }, [focusSection]);
  // Desde WTA y sin guía guardada: se prepara sola, sin pedir otro clic
  useEffect(() => {
    if (focusSection && fetched && !guide && !loading && !err) generate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusSection, fetched]);
  // Guía completa abierta desde WTA: baja hasta el apartado del vector
  useEffect(() => {
    if (!focusSection || !guide || !showAll) return;
    const el = document.getElementById(`guide-sec-${candidate.id}-${focusSection}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [focusSection, guide, showAll, candidate.id]);

  const generate = async () => {
    setLoading(true); setErr("");
    try {
      const qual = (candidate.qualSections || []).map(s => s.section + ": " + (s.items || []).map(i => `${i.q} > ${i.a}`).join(" | ")).join("\n");
      const criteria = sections.map(sec => ({ section: sec.name, items: sec.items.map(it => ({ id: it.id, label: it.label })) }));
      const prompt = `You prepare a SHORT, practical interview guide for a hiring manager.
POSITION: ${position?.title || ""}
CANDIDATE: ${candidate.name}
QUALIFICATION ANSWERS:\n${qual || "Not provided"}
STRENGTHS: ${(candidate.strengths || []).join("; ") || "n/a"}
AREAS TO PROBE: ${(candidate.weaknesses || []).join("; ") || "n/a"}
EVALUATION SECTIONS (one per vector) and the questions the interviewer must score after the interview (1-3 each):
${JSON.stringify(criteria.map((c, i) => ({ sectionId: sections[i].id, ...c })))}

HOW TO WRITE THE QUESTIONS (this matters most):
- 2 to 4 questions PER SECTION, ordered as a natural conversation.
- About PAST EXPERIENCE: real situations, projects, decisions and people from this candidate's career (use the CV and answers: name the actual company, role or project when you can). Never hypotheticals ("what would you do if...") and never self-assessment ("are you a good leader?").
- INDIRECT: the candidate should not be able to tell which trait is being measured. Ask about the story ("tell me about...", "how did ... go", "what happened when..."), not about the skill. Conversational, short, one question at a time.
- Aim them at the areas to probe when relevant.

Return ONLY valid JSON, no markdown:
{"probe":["the areas to probe above, max 3, one short line each"],"intro":["how to open, 1-2 short lines"],"icebreakers":["2-3 small-talk ideas based on real details of this candidate (CV/answers); never invent personal facts"],"sections":[{"id":"<sectionId>","questions":[{"ask":"the question","listen":"what a strong answer sounds like, max 15 words","covers":["<evaluation question ids it helps score>"]}]}],"closing":["1-2 short lines"]}
One entry in "sections" for EVERY sectionId, same order. Plain, direct language. ${es ? "Write everything in Spanish (Spain, tú)." : "Write everything in English."}`;
      let cvUrl = null;
      const storedCv = candidate.cv_url || candidate.cvUrl || "";
      if (storedCv && !/\.docx?(\?|#|$)/i.test(storedCv)) {
        try { cvUrl = await getCvSignedUrl(candidate.id); } catch (e) { /* sin CV se genera igual */ }
      }
      const res = await aiFetch({ body: JSON.stringify({
        max_tokens: 3500,
        messages: [{ role: "user", content: cvUrl ? [{ type: "document", source: { type: "url", url: cvUrl } }, { type: "text", text: prompt }] : prompt }],
      }) });
      const data = await res.json().catch(() => ({}));
      if (res.status === 429) throw new Error(es ? "Has llegado al límite diario de la IA." : "You've reached today's AI limit.");
      if (!res.ok) throw new Error(data?.error?.message || `HTTP ${res.status}`);
      const text = (data.content || []).filter(b => b.type === "text").map(b => b.text).join("");
      const parsed = JSON.parse(text.replace(/```json|```/g, "").trim());
      const next = { ...stored, [key]: parsed };
      const { data: upd, error } = await supabase.from("candidates").update({ interviewer_guide: JSON.stringify(next) }).eq("id", candidate.id).select("id");
      setStored(next);
      // Primera guía: antes de enseñarla, el «Guía lista» con el confeti. Se avisa
      // al padre al terminar (en la entrevista, el aviso es lo que la pinta); si
      // el panel se cierra antes, el aviso sale igual al desmontarse.
      if (!guide) {
        pendingStored.current = JSON.stringify(next);
        setCelebrate(true);
        celebrateTimer.current = setTimeout(() => { setCelebrate(false); flushStored(); }, GUIDE_DONE_MS);
      } else {
        onStored(JSON.stringify(next));
      }
      // Se enseña aunque no se haya podido guardar, pero se dice
      if (error || !upd || upd.length === 0) setErr(es ? "Guía generada, pero no se pudo guardar: se perderá al salir." : "Guide generated but not saved: it will be lost when you leave.");
    } catch (e) {
      console.error("Simple guide error:", e);
      setErr((es ? "No se pudo generar la guía. " : "Couldn't generate the guide. ") + (e.message || ""));
    }
    setLoading(false);
  };

  const box = bare
    ? { boxSizing: "border-box" }
    : isMobile
    ? { ...card, padding: 16, boxSizing: "border-box", minWidth: 0 }
    : embedded
    ? { ...card, boxSizing: "border-box", maxWidth: 820 }
    : { ...card, position: "sticky", top: "calc(var(--pos-head, 0px) + 16px)", maxHeight: "calc(100vh - var(--pos-head, 0px) - 32px)", overflowY: "auto", boxSizing: "border-box" };
  const label = { ...text.label, margin: "16px 0 8px" };
  const bullet = (txt, i) => (
    <div key={i} style={{ display: "flex", gap: 8, marginBottom: 6 }}>
      <div style={{ width: 5, height: 5, borderRadius: "50%", background: BRAND, marginTop: 7, flexShrink: 0 }} />
      <span style={{ fontSize: 13, color: INK, lineHeight: 1.6 }}>{txt}</span>
    </div>
  );
  const byId = Object.fromEntries((guide?.items || []).map(it => [it.id, it]));
  // Guía nueva (desde 11/10): 2-4 preguntas por vector. Las antiguas traían una
  // por criterio (items) y se siguen enseñando, con aviso para regenerar.
  const bySection = Object.fromEntries((guide?.sections || []).map(s => [s.id, s.questions || []]));
  const isNew = !!guide?.sections;
  const shortOf = Object.fromEntries(sections.flatMap(sec => sec.items.map(it => [it.id, it.short])));

  // WTA: solo las preguntas del vector pulsado
  const focusSec = sections.find(x => x.id === focusSection);
  const focusQs = !focusSec ? [] : isNew ? (bySection[focusSec.id] || []) : focusSec.items.map(it => byId[it.id]).filter(Boolean);
  const focusView = focusSec && (
    <div>
      <div style={label}>{es ? "Qué preguntar · " : "What to ask · "}{focusSec.name}</div>
      {focusQs.map((q, qi) => (
        <div key={qi} style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 13, color: INK, lineHeight: 1.55, fontWeight: 600 }}>{q.ask}</div>
          {q.listen && <div style={{ fontSize: 12, color: TEXT, lineHeight: 1.5, marginTop: 2 }}>{es ? "Escucha: " : "Listen for: "}{q.listen}</div>}
          {(q.covers || []).length > 0 && <div style={{ fontSize: 11, color: MUTED, marginTop: 2 }}>{es ? "Ayuda a puntuar: " : "Helps score: "}{q.covers.map(c => shortOf[c] || c).join(", ")}</div>}
        </div>
      ))}
      {focusQs.length === 0 && <div style={{ ...text.bodySm, color: MUTED, marginBottom: 12 }}>{es ? "Sin preguntas para este vector: regenera la guía." : "No questions for this vector: regenerate the guide."}</div>}
      <button onClick={() => setShowAll(true)} style={{ ...linkBtn, ...(isMobile ? { minHeight: 40, fontSize: 13 } : null) }}>
        {es ? "Ver la guía completa" : "See the full guide"}
      </button>
    </div>
  );

  if (celebrate) {
    return (
      <div style={box}>
        <span style={text.cardTitle}>{es ? "Guía de entrevista" : "Interview guide"}</span>
        <GuideLoader lang={lang} done />
      </div>
    );
  }

  return (
    <div style={box}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <span style={text.cardTitle}>{es ? "Guía de entrevista" : "Interview guide"}</span>
        {guide && !loading && (
          <button onClick={generate} style={{ ...linkBtn, color: MUTED, fontWeight: 600, ...(isMobile ? { minHeight: 40, padding: "0 4px", fontSize: 13 } : null) }}>
            {fromOtherLang ? (es ? "Generar en español" : "Generate in English") : (es ? "Regenerar" : "Regenerate")}
          </button>
        )}
      </div>
      {guide && fromOtherLang && (
        <div style={{ fontSize: 12, color: MUTED, marginTop: 4 }}>
          {es ? "Guardada en inglés. Puedes usarla así o generarla en español." : "Saved in Spanish. You can use it as it is or generate it in English."}
        </div>
      )}
      {guide && !fromOtherLang && <div style={{ fontSize: 11, color: MUTED, marginTop: 2 }}>{es ? "Guardada en su ficha: solo se rehace si pulsas Regenerar." : "Saved to their profile: it's only redone if you press Regenerate."}</div>}

      {!guide && (!fetched || loading) && <GuideLoader lang={lang} />}
      {!guide && fetched && !loading && (
        <div style={{ marginTop: 12 }}>
          <p style={{ fontSize: 13, color: MUTED, lineHeight: 1.6, margin: "0 0 14px" }}>
            {es ? "Cómo abrir la entrevista y qué preguntar para poder puntuar cada punto de la evaluación." : "How to open the interview and what to ask so you can score each evaluation point."}
          </p>
          <button onClick={generate} disabled={loading}
            style={{ ...btn.primary, width: "100%", height: isMobile ? 44 : 38, padding: 0, cursor: loading ? "wait" : "pointer" }}>
            {loading ? (es ? "Preparando la guía..." : "Preparing the guide...") : (es ? "Preparar guía" : "Prepare guide")}
          </button>
        </div>
      )}
      {guide && loading && <div style={{ fontSize: 12, color: MUTED, marginTop: 8 }}>{es ? "Regenerando..." : "Regenerating..."}</div>}
      {err && <div style={{ ...errorText, marginTop: 10 }}>{err}</div>}

      {guide && !showAll && focusView}

      {guide && showAll && (
        <div>
          <div style={label}>{es ? "1. Para empezar" : "1. Opening"}</div>
          {(guide.intro || []).map(bullet)}
          {(guide.icebreakers || []).length > 0 && (
            <div style={{ fontSize: 12, color: MUTED, margin: "8px 0 6px" }}>{es ? "Rompehielos" : "Ice breakers"}</div>
          )}
          {(guide.icebreakers || []).map(bullet)}

          {!isNew && (
            <div style={{ fontSize: 12, color: MUTED, background: SURFACE, borderRadius: 8, padding: "8px 10px", margin: "12px 0 0" }}>
              {es ? "Guía en el formato antiguo (una pregunta por criterio). Regenérala para tener 2-4 preguntas por vector sobre su experiencia." : "Guide in the old format (one question per criterion). Regenerate it to get 2-4 experience-based questions per vector."}
            </div>
          )}
          {sections.map((sec, si) => (
            <div key={sec.id} id={`guide-sec-${candidate.id}-${sec.id}`}
              style={focusSection === sec.id ? { background: BRAND_LIGHT, borderRadius: 10, padding: "2px 10px 4px", margin: "8px -10px 0", scrollMarginTop: 8 } : { scrollMarginTop: 8 }}>
              <div style={label}>{si + 2}. {sec.name}</div>
              {isNew && (bySection[sec.id] || []).map((q, qi) => (
                <div key={qi} style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 13, color: INK, lineHeight: 1.55, fontWeight: 600 }}>{q.ask}</div>
                  {q.listen && <div style={{ fontSize: 12, color: TEXT, lineHeight: 1.5, marginTop: 2 }}>{es ? "Escucha: " : "Listen for: "}{q.listen}</div>}
                  {(q.covers || []).length > 0 && <div style={{ fontSize: 11, color: MUTED, marginTop: 2 }}>{es ? "Ayuda a puntuar: " : "Helps score: "}{q.covers.map(c => shortOf[c] || c).join(", ")}</div>}
                </div>
              ))}
              {isNew && !(bySection[sec.id] || []).length && <div style={{ ...text.bodySm, color: MUTED, marginBottom: 12 }}>{es ? "Sin preguntas: regenera la guía." : "No questions: regenerate the guide."}</div>}
              {!isNew && sec.items.map(it => {
                const g = byId[it.id];
                return (
                  <div key={it.id} style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 11, color: MUTED, marginBottom: 3 }}><strong style={{ color: TEXT }}>{it.short}</strong> {it.label}</div>
                    {g ? (
                      <>
                        <div style={{ fontSize: 13, color: INK, lineHeight: 1.55, fontWeight: 600 }}>{g.ask}</div>
                        {g.listen && <div style={{ fontSize: 12, color: TEXT, lineHeight: 1.5, marginTop: 2 }}>{es ? "Escucha: " : "Listen for: "}{g.listen}</div>}
                      </>
                    ) : (
                      <div style={{ ...text.bodySm, color: MUTED }}>{es ? "Sin pregunta: regenera la guía." : "No question: regenerate the guide."}</div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}

          <div style={label}>{sections.length + 2}. {es ? "Para cerrar" : "Closing"}</div>
          {(guide.closing || []).map(bullet)}
        </div>
      )}
    </div>
  );
};

export default SimpleGuide;
